import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileArchive,
  FileSpreadsheet,
  FileText,
  Loader2,
  Minus,
  Plus,
  RotateCcw,
  RotateCw,
} from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { categoriaDoArquivo, extensaoDe, dáParaVisualizar } from "../../helpers/tiposDeArquivo";

import api from "../../services/api";

// Ver o arquivo sem sair da conversa.
//
// Antes, documento na conversa era um botão "Download": para saber se aquele
// PDF era o contrato certo, a pessoa baixava, abria fora do sistema e voltava.
// Aqui o arquivo abre por cima da conversa, com as setas andando pelos outros
// anexos do mesmo atendimento — que é como se procura "aquele comprovante".
//
// O conteúdo é buscado pelo `api` e vira um blob, e não um <img src=URL>, por
// dois motivos: a requisição precisa do token, e os arquivos são servidos com
// `Content-Security-Policy: sandbox` — um <iframe> apontado direto para a URL
// do PDF fica em branco, enquanto o blob: é lido na origem do próprio painel.

const PLANILHAS = [".xls", ".xlsx", ".csv", ".ods"];
const COMPACTADOS = [".zip", ".rar", ".7z", ".gz", ".tgz"];

const IconeGrande = ({ extensao }) => {
  const classe = "h-16 w-16 text-muted-foreground";
  if (PLANILHAS.includes(extensao)) return <FileSpreadsheet className={classe} />;
  if (COMPACTADOS.includes(extensao)) return <FileArchive className={classe} />;
  return <FileText className={classe} />;
};

const nomeParaBaixar = (arquivo) => {
  const extensao = extensaoDe(arquivo.url);
  const data = arquivo.createdAt ? parseISO(arquivo.createdAt) : new Date();

  return `${format(data, "yyyy-MM-dd_HHmm")}_${
    arquivo.fromMe ? "enviada" : "recebida"
  }${extensao}`;
};

const VisualizadorDeArquivo = ({ open, onClose, arquivos = [], indice = 0, onIndice }) => {
  const [blob, setBlob] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [zoom, setZoom] = useState(1);
  // Giro em graus. Foto de documento chega deitada com frequência, e virar a
  // cabeça para ler não é opção.
  const [giro, setGiro] = useState(0);

  const arquivo = arquivos[indice];

  const categoria = useMemo(
    () => (arquivo ? categoriaDoArquivo(arquivo.url, arquivo.mediaType) : null),
    [arquivo],
  );
  const extensao = arquivo ? extensaoDe(arquivo.url) : "";
  const exibivel = arquivo ? dáParaVisualizar(arquivo.url, arquivo.mediaType) : false;

  useEffect(() => {
    if (!open || !arquivo?.url) return undefined;

    let criada = "";
    let cancelado = false;

    const carregar = async () => {
      // Os estados são zerados aqui dentro, e não no corpo do efeito: o
      // eslint (react-hooks/set-state-in-effect) barra setState síncrono no
      // efeito, e trocar de arquivo tem mesmo que limpar o anterior.
      setBlob(null);
      setErro(false);
      setZoom(1);
      setGiro(0);
      setCarregando(true);

      try {
        const { data, headers } = await api.get(arquivo.url, {
          responseType: "blob",
        });

        if (cancelado) return;

        criada = window.URL.createObjectURL(
          new Blob([data], { type: headers["content-type"] }),
        );
        setBlob(criada);
      } catch {
        if (!cancelado) setErro(true);
      } finally {
        if (!cancelado) setCarregando(false);
      }
    };

    carregar();

    return () => {
      cancelado = true;
      // Sem o revoke cada arquivo aberto fica retido até a aba fechar — numa
      // conversa com dezenas de vídeos isso é memória que não volta.
      if (criada) window.URL.revokeObjectURL(criada);
    };
  }, [open, arquivo?.url]);

  const ir = useCallback(
    (passo) => {
      if (!arquivos.length || !onIndice) return;
      const proximo = (indice + passo + arquivos.length) % arquivos.length;
      onIndice(proximo);
    },
    [arquivos.length, indice, onIndice],
  );

  useEffect(() => {
    if (!open) return undefined;

    const aoTeclar = (e) => {
      if (e.key === "ArrowLeft") ir(-1);
      if (e.key === "ArrowRight") ir(1);
    };

    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [open, ir]);

  const baixar = () => {
    if (!blob || !arquivo) return;

    const link = document.createElement("a");
    link.href = blob;
    link.download = nomeParaBaixar(arquivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (!arquivo) return null;

  const legenda = arquivo.legenda || arquivo.body || "";

  const conteudo = () => {
    if (carregando) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (erro || !blob) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
          <p className="text-sm text-muted-foreground">
            Não foi possível carregar este arquivo.
          </p>
          <p className="text-xs text-muted-foreground">
            Ele pode ter sido removido do servidor.
          </p>
        </div>
      );
    }

    if (categoria === "imagem") {
      return (
        <div className="flex h-full items-center justify-center overflow-auto">
          <img
            src={blob}
            alt={legenda || "Arquivo da conversa"}
            style={{ transform: `rotate(${giro}deg) scale(${zoom})` }}
            className="max-h-full max-w-full origin-center object-contain transition-transform"
          />
        </div>
      );
    }

    if (categoria === "video") {
      return (
        <div className="flex h-full items-center justify-center">
          <video src={blob} controls autoPlay className="max-h-full max-w-full rounded-lg" />
        </div>
      );
    }

    if (categoria === "audio") {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <FileText className="h-16 w-16 text-muted-foreground" />
          <audio src={blob} controls autoPlay className="w-full max-w-md" />
        </div>
      );
    }

    if (exibivel) {
      // PDF e texto: o navegador desenha sozinho. `title` é obrigatório para
      // leitor de tela num iframe.
      return (
        <iframe
          src={blob}
          title={legenda || "Documento da conversa"}
          className="h-full w-full rounded-lg border bg-white"
        />
      );
    }

    // Word, Excel, PowerPoint, ZIP: nenhum navegador abre. Dizer isso com
    // todas as letras é melhor que um iframe em branco que parece defeito.
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <IconeGrande extensao={extensao} />
        <p className="text-sm font-medium">
          {extensao ? `Arquivo ${extensao.replace(".", "").toUpperCase()}` : "Arquivo"}
        </p>
        <p className="max-w-sm text-xs text-muted-foreground">
          Este formato não abre dentro do navegador. Baixe para ver no programa
          que o lê.
        </p>
        <Button onClick={baixar}>
          <Download className="h-4 w-4" />
          Baixar arquivo
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="flex h-[90vh] max-w-5xl flex-col gap-3 p-4">
        <div className="flex flex-none items-center gap-2 pr-8">
          <DialogTitle className="min-w-0 flex-1 truncate text-sm font-medium">
            {legenda ||
              (extensao
                ? `Arquivo ${extensao.replace(".", "").toUpperCase()}`
                : "Arquivo da conversa")}
          </DialogTitle>

          {arquivo.createdAt && (
            <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
              {format(parseISO(arquivo.createdAt), "dd/MM/yyyy HH:mm")} ·{" "}
              {arquivo.fromMe ? "enviada" : "recebida"}
            </span>
          )}

          {categoria === "imagem" && blob && (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Diminuir"
                onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-10 text-center text-xs text-muted-foreground">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Aumentar"
                onClick={() => setZoom((z) => Math.min(5, z + 0.25))}
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Girar 90°"
                onClick={() => setGiro((g) => (g + 90) % 360)}
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Restaurar"
                onClick={() => {
                  setZoom(1);
                  setGiro(0);
                }}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            title="Baixar"
            onClick={baixar}
            disabled={!blob}
          >
            <Download className="h-4 w-4" />
          </Button>

          {blob && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              title="Abrir em outra aba"
              asChild
            >
              <a href={blob} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>

        <div className="relative min-h-0 flex-1 rounded-lg bg-muted/40 p-2">
          {conteudo()}

          {arquivos.length > 1 && (
            <>
              <Button
                variant="secondary"
                size="icon"
                className="absolute left-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full opacity-80 hover:opacity-100"
                title="Anterior (seta ←)"
                onClick={() => ir(-1)}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full opacity-80 hover:opacity-100"
                title="Próximo (seta →)"
                onClick={() => ir(1)}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}
        </div>

        {arquivos.length > 1 && (
          <p className={cn("flex-none text-center text-xs text-muted-foreground")}>
            {indice + 1} de {arquivos.length} arquivos desta conversa
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VisualizadorDeArquivo;
